import React, { useEffect, useContext, useState } from 'react';
import { countries, errors, reportingStandards, sectionTitles } from '../../../constants';
import { createID, sortList } from '../../../utils/helper';
import { countryList, getStatesOptions } from '../../../utils/locale';
import { ProfileContext } from '../../../context/Profile.context';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InfoPanel, Label, SectionTitle, SelectField, Table, Tooltip, Typography } from '@otcmarketsgroup/otcm-ui-lib';
import { getIncCorpHistInvalidText, isIncCorpHistValid, isRequiredCountry } from '../../../utils/completes';
import { Trash2, CircleAlert } from 'lucide-react';
import { yearsList } from '../../../constants/years';
import { testIds } from '../../../constants/testIds';

const incorpTestIds = testIds.companyInfo.incorpHistory;

const COUNTRY = 'countryOfIncorporation';
const STATE = 'stateOfIncorporation';
const YEAR = 'yearOfIncorporation';
const PREEXISTING = 'isPreExisting';

const defaultIncorporation = {
    [COUNTRY]: countries.US,
    [STATE]: null,
    [YEAR]: null
};

const IncorporationHistory = () => {
    const [profileState, dispatchProfile] = useContext(ProfileContext);
    const [incorporationView, setIncorporationView] = useState(defaultIncorporation);
    const [viewFormDialog, setFormDialog] = useState(false);
    const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
    const [validationError, setValidationError] = useState(null);

    const profileData = profileState.profile;
    const incorporationList = [...profileData.companyInfo.incorporationInfo];
    const sortedList = sortList(incorporationList, YEAR, 'asc');
    const edgarFilingStatus = profileData.edgarFilingStatus;
    const isValid = isIncCorpHistValid(profileData);
    const isReadOnly = profileState.readOnly;

    const filteredStateList = getStatesOptions(incorporationView, COUNTRY);
    const preExistingList = incorporationList.filter(item => item[PREEXISTING]);
    const isSecFiler = edgarFilingStatus?.includes(reportingStandards.SEC);
    const isCountryReadOnly = isSecFiler && preExistingList && preExistingList.length > 0 ? true : false;
    const isEditing = Boolean(incorporationView.id);

    const columns = [
        {
            header: 'State',
            id: STATE
        },
        {
            header: 'Country',
            id: COUNTRY
        },
        {
            header: 'Year',
            id: YEAR,
            tooltip: {
                subtitle: 'Year company became registered in Jurisdiction.',
                dark: true
            }
        },
        ...(!isReadOnly ? [{
            cellProps: {
                align: 'right'
            },
            width: '75px',
            renderCell: item => <IconButton color='primary' data-testid={incorpTestIds.deleteBtn(item.id)} onClick={e => handleRemoveRow(e, item)}><Trash2 size={20} /></IconButton>
        }] : [])
    ];


    useEffect(() => {
        if (!viewFormDialog) {
            setIncorporationView(defaultIncorporation);
            setValidationError(null);
        }
    }, [viewFormDialog]);

    const onValueChanged = (field, value) => {
        setIncorporationView(prev => ({
            ...prev,
            [field]: value || (field === COUNTRY ? countries.US : null),
            ...(field === COUNTRY && prev[COUNTRY] !== value ? { [STATE]: null } : {}),
        }));
    };

    const openAddNew = () => {
        const newIncorpObj = { ...defaultIncorporation };
        const preExistingList = sortedList.filter(item => item[PREEXISTING]);
        if (isSecFiler && preExistingList && preExistingList.length > 0) {
            const index = preExistingList.length - 1;
            newIncorpObj[COUNTRY] = preExistingList[index][COUNTRY];
        }

        setIncorporationView(newIncorpObj);
        setFormDialog(true);
    };

    const handleRowClick = (e, item) => {
        setIncorporationView({ ...item });
        setFormDialog(true);
    };

    const handleRemoveRow = (e, item) => {
        e.stopPropagation();

        setOpenRemoveDialog(item);
    };

    const confirmRemoveRow = () => {
        const updatedObj = { ...profileData.companyInfo };
        updatedObj.incorporationInfo = updatedObj.incorporationInfo.filter(obj => obj.id !== openRemoveDialog.id);

        dispatchProfile({
            type: 'UPDATE_PROFILE',
            field: 'companyInfo',
            payload: updatedObj
        });
        setOpenRemoveDialog(false);
    };

    const formDialogClose = () => {
        setFormDialog(false);
        setIncorporationView(defaultIncorporation);
    };

    const handleAdd = () => {
        setValidationError(null);

        if (!validateForm()) return;

        const updatedCompanyInfo = { ...profileData.companyInfo };
        updatedCompanyInfo.incorporationInfo = [
            ...updatedCompanyInfo.incorporationInfo,
            { ...incorporationView, id: createID(updatedCompanyInfo.incorporationInfo) },
        ];
        dispatchProfile({ type: 'UPDATE_PROFILE', field: 'companyInfo', payload: updatedCompanyInfo });
        formDialogClose();
    };

    const handleUpdate = () => {
        setValidationError(false);
        if (!validateForm()) return;

        const updatedObj = { ...profileData.companyInfo };

        updatedObj.incorporationInfo = updatedObj.incorporationInfo.map(item => {
            if (item.id === incorporationView.id) return incorporationView;

            return item;
        });

        dispatchProfile({
            type: 'UPDATE_PROFILE',
            field: 'companyInfo',
            payload: updatedObj
        });

        formDialogClose();
    };

    const validateForm = () => {
        const isStateValid = incorporationView[COUNTRY] === countries.AU
            || incorporationView[COUNTRY] === countries.CA
            || incorporationView[COUNTRY] === countries.US;

        if (!incorporationView[COUNTRY] || (isStateValid && !incorporationView[STATE]) || !incorporationView[YEAR]) {
            setValidationError(errors.INCOMPLETE_DATA);
            return false;
        }

        const hasExist = incorporationList.filter(item => ((!item[STATE] && !incorporationView[STATE]) || (item[STATE] === incorporationView[STATE]))
            && item[COUNTRY] === incorporationView[COUNTRY]
            && item[YEAR] === incorporationView[YEAR]);

        if (hasExist && hasExist.length > 0) {
            setValidationError(errors.DUPLICATE_DATA);
            return false;
        }

        return true;
    };

    return <Box>
        <Tooltip dark disableHoverListener={isValid} subtitle={getIncCorpHistInvalidText(profileData)} placement='top-start'>
            <Box display='flex' justifyContent='space-between' alignItems='center' sx={{ borderBottom: !isValid ? '3px solid' : 'none', borderColor: 'var(--otc-warning)', mb: isValid ? 0 : 2 }}>
                <Box display='flex' alignItems='center' gap={1}>
                    {!isValid && <CircleAlert color='var(--otc-warning)' size={16} />}
                    <SectionTitle noUnderline>{sectionTitles.INCORP_HISTORY}</SectionTitle>
                </Box>
                {!isReadOnly && <Button variant='text' data-testid={incorpTestIds.addBtn} onClick={openAddNew}>
                    Add New Incorporation History
                </Button>}
            </Box>
        </Tooltip>
        <Typography variant='body1' mb={2}>
            Enter the incorporation or registration information for the issuer or its' predecessors (if any) during the past five years.
        </Typography>
        <Box data-testid={incorpTestIds.table}>
            <Table columns={columns} data={sortedList} handleRowClick={!isReadOnly ? (e, item) =>  handleRowClick(e, item) : undefined} />
        </Box>
        <Dialog open={viewFormDialog} onClose={formDialogClose} data-testid={incorpTestIds.dialog}>
            <DialogTitle title='Incorporation History' onClose={formDialogClose} />
            <DialogContent>
                <Box display='grid' gap={2} gridTemplateColumns={{ desktop: 'repeat(6, 1fr)', mobile: '1fr' }}>
                    <Box gridColumn='span 3' data-testid={incorpTestIds.country}>
                        <Label tooltipDark tooltipSubtitle={isCountryReadOnly ? "To make a modification in the Company's Country of Incorporation - please make a note on the Review and Submit page." : undefined}>Country of Incorporation</Label>
                        <SelectField
                            value={incorporationView?.[COUNTRY]}
                            options={countryList}
                            readOnly={isCountryReadOnly}
                            onChange={e => onValueChanged(COUNTRY, e.target.value)}
                        />
                    </Box>
                    <Box gridColumn='span 3' data-testid={incorpTestIds.state}>
                        <SelectField
                            label="State/Province of Incorporation"
                            value={incorporationView?.[STATE]}
                            options={filteredStateList}
                            disabled={!isRequiredCountry(incorporationView[COUNTRY]) && filteredStateList?.length === 0}
                            error={validationError && isRequiredCountry(incorporationView[COUNTRY]) && !incorporationView?.[STATE]}
                            helperText={validationError && isRequiredCountry(incorporationView[COUNTRY]) && !incorporationView?.[STATE] ? 'Required' : undefined}
                            onChange={e => onValueChanged(STATE, e.target.value)}
                        />
                    </Box>
                    <Box gridColumn='span 3' data-testid={incorpTestIds.year}>
                        <SelectField
                            label="Year of Incorporation"
                            value={incorporationView?.[YEAR]}
                            options={yearsList}
                            error={validationError && !incorporationView?.[YEAR]}
                            helperText={validationError && !incorporationView?.[YEAR] ? 'Required' : undefined}
                            onChange={e => onValueChanged(YEAR, e.target.value)}
                        />
                    </Box>
                </Box>
                {validationError && <Box mt={2}>
                    <InfoPanel title={validationError} isWarning />
                </Box>}
            </DialogContent>
            <DialogActions>
                <Button variant='outlined' data-testid={incorpTestIds.dialogCancelBtn} onClick={formDialogClose}>Cancel</Button>
                <Button data-testid={incorpTestIds.dialogSubmitBtn} onClick={isEditing ? handleUpdate : handleAdd}>{isEditing ? 'Update' : 'Add'}</Button>
            </DialogActions>
        </Dialog>

        <Dialog open={openRemoveDialog} onClose={() => setOpenRemoveDialog(false)} data-testid={incorpTestIds.removeDialog}>
            <DialogTitle title='Remove Incorporation History' onClose={() => setOpenRemoveDialog(false)} />
            <DialogContent>
                <Typography variant='body1' component='p' gutterBottom>
                    Are you sure you want to delete?
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button variant='outlined' color='warning' data-testid={incorpTestIds.removeCancelBtn} onClick={() => setOpenRemoveDialog(false)}>
                    Cancel
                </Button>
                <Button color='warning' data-testid={incorpTestIds.removeConfirmBtn} onClick={confirmRemoveRow}>
                    Yes
                </Button>
            </DialogActions>
        </Dialog>
    </Box>;
};

export default IncorporationHistory;
